import { useEffect, useState, useRef, useCallback } from "react";
import {
    Button, Container, TextField, Grid, MenuItem, Autocomplete, Box, CircularProgress
} from "@mui/material"
import Select, { SelectChangeEvent } from "@mui/material/Select"
import { ComparePageHead } from "./components/styles/compare.styled";
import { EntrySection, SavingRow } from "./components/styles/common.styled";
import { CompareChart } from "./components/CompareChart";
import { CompareAside } from "./components/CompareAside";
import {
    fetchAddress, fetchUtility, fetchTerms, fetchPlan,
    fetchEstUsage, fetchPremiseDetail, fetchConstellationPlans, fetchThreeHeightsPlan, fetchFliterMonth
} from "./fetch/fetch";
import { PlanTypes, ChartDataTypes, PremiseDetailProps, AddressProps, UtilityTypes, PlanProps } from "./types";
import { PROVIDER_ID, PROVIDER_NAME, REPs, ThreeReps, Tier } from "./config";
import { getCurrentPromoCode } from "./utils/promoCodeUtils";
import { RollingNumber } from './components/RollingNumber';
import { GetUsageSidebar } from "./components/GetUsageSidebar";
import { MyAddressContext } from './context-manager';
import throttle from 'lodash.throttle';
import { withSnackbar } from './components/SnackbarHOC';
import { PlanModal } from "./components/enrollment/PlanModal";
import { useLocation, useNavigate } from "react-router-dom";
import qs from 'qs';
import { getBestRate } from "./components/data";

const Compare = ({
    snackbarShowMessage
}: {
    snackbarShowMessage?: Function;
}) => {
    const [address, setAddress] = useState('');
    const [selectedTerm, setSelectedTerm] = useState('');
    const [addressOptions, setAddressOptions] = useState([]);
    const [addressLoading, setAddressLoading] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState<AddressProps | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [utilities, setUtilities] = useState<UtilityTypes[]>([]);
    const [selectedUtility, setSelectedUtility] = useState('');
    const [termOptions, setTermOptions] = useState([]);
    const [chartData, setChartData] = useState<ChartDataTypes[]>([]);
    const [usages, setUsages] = useState<number[]>([]);
    const [isValidAddress, setIsValidAddress] = useState(false);
    const [premiseDetail, setPremiseDetail] = useState<PremiseDetailProps | undefined>(undefined);
    const [premiseLoading, setPremiseLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [oneRepPlanList, setOneRepPlanList] = useState<PlanProps[]>([]);
    const [plans, setPlans] = useState<{ [key: string]: PlanProps[] } | undefined>(undefined);
    const [consentId, setConsentId] = useState('');
    const [sourceFrom, setSourceFrom] = useState('');
    const [showGetUsageSidebar, setShowGetUsageSidebar] = useState(false);
    const [isEstMode, setIsEstMode] = useState(true);
    const mainRef = useRef<null | HTMLDivElement>(null);
    const [products, setProducts] = useState<PlanProps[]>([]);
    const [threeHeightPlans, setThreeHeightPlans] = useState<{ [key: string]: PlanProps[] } | undefined>();
    const [isThreePlan, setIsThreePlan] = useState(false);
    const [chartLoading, setChartLoading] = useState(false);
    const location = useLocation();
    const queryParams = qs.parse(location.search, {
        ignoreQueryPrefix: true,
    });

    const navigate = useNavigate();

    let promo_code = queryParams.promo_code as string || getCurrentPromoCode(location.pathname);
    const fetchAddressDelayed = useCallback(
        throttle((text, callback) => {
            if (text && text.trim()) {
                setAddressLoading(true);
                fetchAddress(text.trim()).then(callback);
            }
        }, 1000),
        []
    )

    const handleChangeAddress = (evt: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = evt.target;
        setAddress(value);
        setIsValidAddress(false);
        setUtilities([]);
        setSelectedUtility('');
        setSelectedTerm('');
        setChartData([]);
    }

    useEffect(() => {
        fetchAddressDelayed(address, (result: any) => {
            setAddressLoading(false);
            setAddressOptions(result || []);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [address])

    const handleGetData = () => {
        if (!selectedAddress) return;

        localStorage.setItem('selectedAddress', JSON.stringify(selectedAddress));
        const { zipcode, street } = selectedAddress;
        setLoading(true);
        getEstUsage();
        setPremiseLoading(true);

        const utilityCode = findUtilityCode();

        if (utilityCode) {
            fetchFliterMonth(zipcode, utilityCode, promo_code).then(res => {
                if (res && Number(res.status) === 1) {
                    let terms = res.response.terms;
                    terms.sort((a: string, b: string) => {
                        if (parseInt(a) < parseInt(b)) return -1;
                        if (parseInt(a) > parseInt(b)) return 1;
                        return 0;
                    });
                    setTermOptions(terms);
                    if (terms.length > 0) {
                        if (terms.indexOf('12') >= 0) {
                            setSelectedTerm('12');
                        }
                        else {
                            setSelectedTerm(terms[0]);
                        }
                    }
                }
                else {
                    snackbarShowMessage?.(res.msg, 'error');
                    setLoading(false);
                }
            })
        }

        fetchPremiseDetail(street, zipcode).then(res => {
            setPremiseLoading(false);
            setPremiseDetail((res && res.premise && res.premise.detail) ? res.premise.detail : undefined);
        })
    }


    const getProducts = (myUsage?: Number[]) => {
        setLoading(true);
        const utilityCode = findUtilityCode();
        const term = selectedTerm ? selectedTerm : '12';
        const zipcode = selectedAddress?.zipcode;
        const provider_id = PROVIDER_ID;
        const usages = new Array(12).fill(parseInt('1000'));

        fetchConstellationPlans(
            zipcode,
            utilityCode,
            term,
            provider_id,
            myUsage ? myUsage : usages,
            promo_code
        ).then((res) => {
            if (res && Number(res.status) === 1) {
                const { plans } = res.response;
                if (plans) {
                    var _plans = plans.filter((item: any) => item.plan_name.indexOf('Flat Tier Product') === -1)
                    setProducts(_plans);
                    setLoading(false);
                }
            } else {
                setProducts([]);
            }
        })
    }

    const findUtilityCode = () => {
        if (!selectedUtility) return '';

        return utilities.filter((val) => val.utility_name === selectedUtility)[0].utility_code;
    }

    const getUtility = () => {
        if (!selectedAddress) return;

        const { zipcode } = selectedAddress;

        fetchUtility(zipcode).then(res => {
            setLoading(false);
            if (res && res[0] && res[0].utility_code) {
                setUtilities(res);

                if (res[0] && res[0].utility_code && !selectedUtility) {
                    setSelectedUtility(res[0].utility_name);
                }
            }
            else {
                snackbarShowMessage?.(res.message, 'error');
            }
        })
    }

    useEffect(() => {
        setIsThreePlan(false);
        getUtility();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAddress])

    const getEstUsage = () => {
        if (!selectedAddress) return;

        const { zipcode, city, state, street } = selectedAddress;
        fetchEstUsage(street, city, state, zipcode).then(res => {
            if (res && res.status === 1) {
                const { usages } = res;
                const usageArr = Object.keys(usages).map(key => usages[key]);
                setUsages(usageArr);
            }
        })
    }

    const handleScroll = () => {
        if (mainRef && mainRef.current) {
            mainRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    useEffect(() => {
        if (selectedTerm && usages.length > 0) {
            setChartLoading(true);
            setChartData([])
            setIsThreePlan(false);
            setPlans(undefined);
            setProducts([]);
            getPlan(usages);
            getProducts(usages);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTerm, usages])

    const getPlan = (myUsage: Number[]) => {
        setLoading(true);
        const usages = new Array(12).fill(parseInt('1000'));
        const utilityCode = findUtilityCode();

        fetchPlan(
            parseInt(selectedTerm),
            utilityCode,
            myUsage ? myUsage : usages
        ).then(res => {
            setLoading(false);
            if (res && res.code === 1) {
                const allPlans = res.response;
                setPlans(allPlans);
                // setTimeout(() => {
                //     handleScroll();
                // }, 200);
            }
            if (res && res.code === 0) {
                getThreeHeightsPlan();
            }
        })
    }

    const getThreeHeightsPlan = (type?: number) => {
        const utilityCode = findUtilityCode();
        setIsThreePlan(true);
        fetchThreeHeightsPlan(
            parseInt(selectedTerm ? selectedTerm : '12'),
            utilityCode,
            usages
        ).then(res => {
            setLoading(false);
            if (res && res.code === 1) {
                const allPlans = res.response;
                setThreeHeightPlans(allPlans);
                // setTimeout(() => {
                //     handleScroll();
                // }, 200);
            }
            else {
                snackbarShowMessage?.(res.msg, 'error');
            }
        })
        return plans;
    }

    useEffect(() => {
        getAverageRate();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products, plans])



    const getAverageRate = () => {
        if (!plans) {
            return
        }
        if (products.length !== 0 && plans) {
            let allPlans: any = plans;

            // averageRate
            // let total_price = 0;
            // products.forEach((val: any, idx: number) => {
            //     total_price += val.rate;
            // })

            let total_price = products.sort((a, b) => { return a.rate - b.rate })[0].rate

            let data = [];
            for (let i in allPlans) {
                const _plans = allPlans[i];

                let totalRate = 0;
                _plans.forEach((val: PlanTypes, idx: number) => {
                    const { total_rate } = val;
                    totalRate += total_rate;
                });
                if (i !== PROVIDER_ID) {
                    data.push({
                        id: i,
                        value: Math.ceil(totalRate / _plans.length * 100) / 100,
                        label: _plans[0].provider_name || REPs.filter((item) => item.providerId === i)[0].providerName
                    });
                }
            }

            // if (data.filter((item) => item.value >= Math.ceil(total_price / products.length * 100) / 100).length <= 1) {
            if (data.filter((item) => item.value >= total_price).length <= 0) {
                getThreeHeightsPlan();
            } else {
                data.sort((a, b) => {
                    if (a.value < b.value) return -1;
                    if (a.value > b.value) return 1;
                    return 0;
                });
                let _chartData: any = [];

                _chartData.push({
                    id: PROVIDER_ID,
                    // value: Math.ceil(total_price / products.length * 100) / 100,
                    value: total_price,
                    label: PROVIDER_NAME
                })

                _chartData = _chartData.concat(data.slice(-3));

                setChartData(_chartData.filter((item: any) => item.value >= _chartData[0].value));
                setChartLoading(false);
                setTimeout(() => {
                    handleScroll();
                }, 200);
            }
        }
    }
    useEffect(() => {
        getThreeHeightsRate();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [threeHeightPlans, products])


    const getThreeHeightsRate = (flag?: boolean) => {

        if (products.length === 0 || !threeHeightPlans || chartData.length !== 0) return;

        let total_price = products.sort((a, b) => { return a.rate - b.rate })[0].rate;
        let _allplans: any = threeHeightPlans;

        const data = [];
        for (let i in _allplans) {
            if (ThreeReps.includes(Number(i)) || flag) {
                const _plans = _allplans[i];
                let totalRate = 0;
                _plans.forEach((val: PlanTypes, idx: number) => {
                    const { total_rate } = val;
                    totalRate += total_rate;
                });
                if (i !== PROVIDER_ID) {
                    data.push({
                        id: i,
                        value: Math.ceil(totalRate / _plans.length * 100) / 100,
                        label: _plans[0].provider_name
                    });
                }
            } else {
                continue;
            }
        }

        if (data.length === 0) {
            for (let i in _allplans) {
                if (Tier.includes(Number(i)) || flag) {
                    const _plans = _allplans[i];
                    let totalRate = 0;
                    _plans.forEach((val: PlanTypes, idx: number) => {
                        const { total_rate } = val;
                        totalRate += total_rate;
                    });
                    if (i !== PROVIDER_ID) {
                        data.push({
                            id: i,
                            value: Math.ceil(totalRate / _plans.length * 100) / 100,
                            label: _plans[0].provider_name
                        });
                    }
                } else {
                    continue;
                }
            }
            // getThreeHeightsRate(true);
            // return;
        }
        data.sort((a, b) => {
            if (a.value < b.value) return -1;
            if (a.value > b.value) return 1;
            return 0;
        });

        let _chartData: any = [];

        _chartData.push({
            id: PROVIDER_ID,
            // value: Math.ceil(total_price / products.length * 100) / 100,
            value: total_price,
            label: PROVIDER_NAME
        })

        _chartData = _chartData.concat(data.slice(-3));

        setChartData(_chartData.filter((item: any) => item.value >= _chartData[0].value));
        setChartLoading(false);
        setTimeout(() => {
            handleScroll();
        }, 200);
    }


    const handleSetChartData = (data: ChartDataTypes[]) => {
        let arr = data;

        arr.sort((a, b) => {
            if (a.value < b.value) return -1;
            if (a.value > b.value) return 1;
            return 0;
        });

        let _chartData = [];
        const hostRep = REPs.filter(val => val.host === true);
        const hostData = arr.filter(val => val.id === hostRep[0].providerId);

        if (hostData[0]) {
            _chartData.push(hostData[0]);
        }

        const hostDataIdx = arr.findIndex(val => val.id === hostData[0].id);
        arr.splice(hostDataIdx, 1);
        _chartData = _chartData.concat(arr.slice(-3));

        setChartData(_chartData.filter((item) => item.value >= hostData[0].value));
    }

    let energySavings = 0;
    if (usages && chartData && chartData.length > 0) {
        const rateDiff = chartData[chartData.length - 1].value - chartData[0].value;
        const totalUsage = usages.reduce((partialSum, a) => partialSum + a, 0);
        energySavings = rateDiff * (totalUsage / 12) * parseInt(selectedTerm) / 100;
    }

    return (
        <>
            <EntrySection style={{ padding: '44px 0', background: 'unset' }}>
                <Container sx={{ pt: 3, pb: 3, pl: 6, pr: 6, backgroundColor: '#fff' }}>
                    <ComparePageHead>
                        <h2>For a Personalized Savings Calculation</h2>
                        <p style={{ color: '#999', fontSize: 14 }}>(Enter your home address)</p>
                    </ComparePageHead>
                    <Grid container spacing={3} sx={{ mt: 2 }} justifyContent="center">
                        <Grid item xs={12} sm={8}>
                            <Autocomplete
                                freeSolo
                                onChange={(evt, val) => {
                                    setAddress(val ? val.label : '');
                                    setSelectedAddress(val);
                                    setIsValidAddress(true);
                                }}
                                options={addressOptions.map((option: any) => option)}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.id}>{option.label}</li>
                                )}
                                loading={addressLoading}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label="Please Enter Your Address"
                                        value={address}
                                        onChange={handleChangeAddress}
                                        disabled={loading}
                                    />
                                )}
                            />
                        </Grid>
                        {
                            (utilities && utilities.length > 1) &&
                            <Grid item xs={12} sm={4}>
                                <Select
                                    labelId="utility-label"
                                    id="utility"
                                    value={selectedUtility}
                                    onChange={(evt: SelectChangeEvent) => {
                                        setSelectedUtility(evt.target.value as string);
                                    }}
                                    disabled={loading}
                                    fullWidth
                                    sx={{ textAlign: 'left' }}
                                >
                                    {
                                        utilities.map((val: any, idx) => <MenuItem value={val.utility_name} key={idx}>{val.utility_name}</MenuItem>)
                                    }
                                </Select>
                            </Grid>
                        }
                    </Grid>
                    <div style={{ textAlign: 'center', marginTop: 24 }}>
                        <Button
                            size="large"
                            variant="contained"
                            onClick={handleGetData}
                            disabled={loading || !isValidAddress || !selectedUtility}
                        >Continue</Button>
                    </div>
                </Container>
            </EntrySection>
            {
                selectedTerm &&
                <Container sx={{ backgroundColor: '#fff', padding: '44px 20px' }}>
                    <div ref={mainRef}>
                        <Grid container spacing={4}>
                            <Grid item xs={12} sm={4}>
                                <CompareAside
                                    premiseLoading={premiseLoading}
                                    address={selectedAddress ? selectedAddress.label : ''}
                                    premiseDetail={premiseDetail}
                                    usages={usages}
                                    handleShowSidebar={() => setShowGetUsageSidebar(true)}
                                    handleGetEstUsage={() => {
                                        getEstUsage();
                                        setIsEstMode(true);
                                    }}
                                    handleChangeUsage={(usages: number[]) => {
                                        setUsages(usages);
                                        setIsEstMode(true);
                                    }}
                                    isEstMode={isEstMode}
                                    sourceFrom={sourceFrom}
                                />
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                                    <h2 style={{ marginTop: 0 }}>Estimated Total Savings = <RollingNumber data={energySavings} styles={{ color: '#2372B9', fontWeight: 700 }} /></h2>
                                    {
                                        !isEstMode &&
                                        <p>(Based on personalized historic usage)</p>
                                    }
                                </div>
                                <Grid container>
                                    <Grid container item xs={6} alignItems="center">
                                        {/* <SavingRow>Energy Savings: <NumberFormat value={energySavings} displayType={'text'} thousandSeparator={true} prefix={'$'} decimalScale={2} fixedDecimalScale={true} /></SavingRow> */}
                                    </Grid>
                                    <Grid container item xs={6} alignItems="center" justifyContent="flex-end">
                                        <span style={{ marginRight: 12 }}>Term:</span>
                                        <Select
                                            labelId="term-select-label"
                                            id="term-select"
                                            value={selectedTerm}
                                            onChange={(evt: SelectChangeEvent) => {
                                                setSelectedTerm(evt.target.value as string);
                                            }}
                                            disabled={loading}
                                        >
                                            {
                                                termOptions.map((val, idx) => <MenuItem value={val} key={idx}>{`${val} Months`}</MenuItem>)
                                            }
                                        </Select>
                                    </Grid>
                                </Grid>
                                {chartLoading && <Grid container alignItems="center" justifyContent="center">
                                    <CircularProgress style={{ margin: '100px 0' }} />
                                </Grid>}
                                {!chartLoading && <CompareChart
                                    chartData={chartData}
                                    handleSignUp={(id: string) => {
                                        setShowModal(false);
                                        const hostRep = REPs.filter((val) => val.host === true);
                                        if (hostRep[0] && hostRep[0].providerId === id) {
                                            setOneRepPlanList(products);
                                            const BestRate = getBestRate(promo_code);
                                            let bestRatePlan: any = [];
                                            bestRatePlan = products
                                                .filter((item) => BestRate.indexOf(Number(item.plan_id)) > -1)
                                                .sort((a, b) =>
                                                    Number(a.contract_term) < Number(b.contract_term) ? -1 : 1
                                                );
                                            localStorage.setItem("selectProduct", JSON.stringify(bestRatePlan[0]));
                                            navigate(`/v2/enrollment${location.search}`);
                                        }
                                    }}
                                    handleShowPlan={(id: string) => {
                                        setShowModal(true);
                                        const hostRep = REPs.filter(val => val.host === true);
                                        if (hostRep[0] && hostRep[0].providerId === id) {
                                            setOneRepPlanList(products);
                                        }
                                        else {
                                            if (plans && plans[id] && !isThreePlan) {
                                                setOneRepPlanList(plans[id]);
                                            }
                                            if (threeHeightPlans && threeHeightPlans[id] && isThreePlan) {
                                                setOneRepPlanList(threeHeightPlans[id]);
                                            }
                                        }
                                    }}
                                />}
                                <div style={{ textAlign: 'center', marginTop: 40, marginBottom: 20 }}>
                                    <Button size="large" variant="contained" onClick={() => {
                                        setShowModal(true);
                                        setOneRepPlanList(products);
                                        // window.location.href = "https://www.constellation.com/solutions/for-your-home/residential-signup.html?zip=&promoCode=ROCKSTAR"
                                    }}>Sign Up</Button>
                                </div>
                            </Grid>
                        </Grid>
                    </div>
                </Container>
            }
            <PlanModal
                show={showModal}
                data={oneRepPlanList}
                handleClose={() => { setShowModal(false); }}
                selectProduct={() => { }}
                promoCode={promo_code}
            />
            <MyAddressContext.Provider value={{
                myAddress: selectedAddress,
                utilityCode: findUtilityCode(),
                sourceFrom: sourceFrom,
                handleSetUsage: (val: { usage: number[], source: string }) => {
                    setUsages(val.usage);
                    setSourceFrom(val.source);
                    if (val.source) {
                        setIsEstMode(false);
                    }
                    setShowGetUsageSidebar(false);
                }
            }}>
                <GetUsageSidebar
                    show={showGetUsageSidebar}
                    handleClose={() => {
                        setShowGetUsageSidebar(false);
                    }}
                    handleSetConsentId={(val: string) => setConsentId(val)}
                    zipcode={selectedAddress ? selectedAddress.zipcode : ''}
                />
            </MyAddressContext.Provider>
        </>
    )
}

export default withSnackbar(Compare)